import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Paper,
  Typography,
  useTheme,
  Box,
  LinearProgress,
  alpha,
  Collapse,
  IconButton,
  Chip,
  Fade,
  Badge,
  Stack,
  Divider,
  Avatar,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CategoryIcon from '@mui/icons-material/Category';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import PlaceIcon from '@mui/icons-material/Place';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useAuth } from '../../context/AuthContext';
import { analyticsApi, assetsApi } from '../../services/api';
import { exportToCsv } from '../../utils/exportToCsv';

// Color palettes for donut charts
const CHART_COLORS = {
  slopeTypes: ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb', '#e3f2fd'],
  cutEmbankment: {
    'Cut Slope': '#1976d2',    // Blue for Cut
    'Embankment': '#ff9800',   // Orange for Embankment
    'Not Specified': '#9e9e9e' // Grey for unspecified
  },
  ratings: {
    'Excellent': '#4caf50',
    'Good': '#8bc34a',
    'Fair': '#ffc107',
    'Poor': '#ff9800',
    'Severe': '#f44336',
    'Unrated': '#9e9e9e'
  },
  inspectionStatus: {
    'Inspected': '#4caf50',
    'Uninspected': '#f44336'
  }
};

// Helper functions to prepare chart data
// Determine a numeric order for rating levels. Prefer explicit DB value if present,
// otherwise parse from names like Rx5/Cx5, and finally fall back to a semantic map.
const getRatingOrderValue = (item) => {
  // 1) Prefer backend-provided numeric value fields if available
  const candidateValues = [item.value, item.rating_value, item.ratingLevelValue];
  for (const v of candidateValues) {
    const num = typeof v === 'string' ? parseInt(v, 10) : Number(v);
    if (Number.isFinite(num)) return num;
  }

  // 2) Try parsing from rating name formats: Rx5, R5, Cx5, C5
  const rawLabel = (item.rating_name || item.name || '').toString();
  const match = rawLabel.match(/(?:^|\s)(?:R|C)x?(\d+)/i);
  if (match && match[1]) {
    const parsed = parseInt(match[1], 10);
    if (Number.isFinite(parsed)) return parsed;
  }

  // 3) Fallback semantic mapping for textual names
  const map = {
    'unrated': 0,
    'excellent': 1,
    'good': 2,
    'fair': 3,
    'poor': 4,
    'severe': 5
  };
  const key = rawLabel.trim().toLowerCase();
  return map[key] ?? -1; // Unknowns go to the bottom
};

const prepareSlopeTypeChartData = (slopeTypeBreakdown) => {
  return slopeTypeBreakdown.map((item, index) => {
    const rawCount = item.count;
    const numericCount = typeof rawCount === 'string' ? parseInt(rawCount, 10) : Number(rawCount ?? 0);
    return {
      name: item.subtype_name,
      value: Number.isFinite(numericCount) ? numericCount : 0,
      color: CHART_COLORS.slopeTypes[index % CHART_COLORS.slopeTypes.length]
    };
  });
};

const prepareRatingChartData = (ratingBreakdown) => {
  return ratingBreakdown.map(item => {
    const label = (item.rating_name || item.name || '').replace('Cx', 'Rx');
    const rawCount = item.count;
    const numericCount = typeof rawCount === 'string' ? parseInt(rawCount, 10) : Number(rawCount ?? 0);
    const dbColor = (item.color_code || item.colorCode || item.color || '').toString().trim();
    return {
      name: label,
      value: Number.isFinite(numericCount) ? numericCount : 0,
      color: dbColor || CHART_COLORS.ratings[item.rating_name || item.name] || '#9e9e9e'
    };
  });
};

const prepareInspectionStatusChartData = (totalSlopes, uninspectedCount) => {
  const inspectedCount = totalSlopes - uninspectedCount;
  return [
    {
      name: 'Inspected',
      value: inspectedCount,
      color: CHART_COLORS.inspectionStatus.Inspected
    },
    {
      name: 'Uninspected',
      value: uninspectedCount,
      color: CHART_COLORS.inspectionStatus.Uninspected
    }
  ].filter(item => item.value > 0);
};

// Prepare Cut/Embankment breakdown chart data
const prepareCutEmbankmentChartData = (cutEmbankmentBreakdown) => {
  if (!cutEmbankmentBreakdown || cutEmbankmentBreakdown.length === 0) return [];
  return cutEmbankmentBreakdown.map(item => {
    const rawCount = item.count;
    const numericCount = typeof rawCount === 'string' ? parseInt(rawCount, 10) : Number(rawCount ?? 0);
    return {
      name: item.slope_type_name,
      code: item.slope_type_code,
      value: Number.isFinite(numericCount) ? numericCount : 0,
      color: CHART_COLORS.cutEmbankment[item.slope_type_name] || '#9e9e9e'
    };
  });
};

// Prepare uninspected breakdown chart data (by subtype)
const prepareUninspectedBreakdownChartData = (uninspectedBySubtype) => {
  if (!uninspectedBySubtype || uninspectedBySubtype.length === 0) return [];
  return uninspectedBySubtype.map((item, index) => {
    const rawCount = item.count;
    const numericCount = typeof rawCount === 'string' ? parseInt(rawCount, 10) : Number(rawCount ?? 0);
    return {
      name: item.subtype_name || item.network_name || item.slope_type_name,
      value: Number.isFinite(numericCount) ? numericCount : 0,
      color: CHART_COLORS.slopeTypes[index % CHART_COLORS.slopeTypes.length]
    };
  });
};

// Function to generate slope data from real stats
const getSlopeDataFromStats = (stats, loading) => {
  if (loading) {
    return {
      totalSlopes: { id: 'total_slopes', title: "Total Slopes", count: '...', progress: 0, description: "Loading data...", slogan: "Infrastructure Assets", Icon: AccountBalanceIcon, color: 'primary.main', trend: '-', featured: true },
      slopeTypes: { id: 'slope_types', title: "Slope Subtypes", count: '...', progress: 0, description: "Loading data...", Icon: CategoryIcon, color: 'info.main', trend: '-' },
      severeSlopes: { id: 'severe_slopes', title: "Hazard Ranking", count: '...', progress: 0, description: "Loading data...", Icon: WarningAmberIcon, color: 'error.main', trend: '-' },
      uninspected: { id: 'uninspected_slopes', title: "Uninspected Slopes", count: '...', progress: 0, description: "Loading data...", Icon: AssignmentLateIcon, color: 'warning.main', trend: '-' },
      networkSevere: { id: 'network_severe', title: "Highest-Risk Network", count: '...', progress: 0, description: "Loading data...", Icon: PlaceIcon, color: 'secondary.main', trend: '-' },
      sectionSevere: { id: 'section_severe', title: "Highest-Risk Section", count: '...', progress: 0, description: "Loading data...", Icon: LocationOnIcon, color: 'secondary.main', trend: '-' },
    };
  }

  const totalSlopes = stats.totalSlopes || 0;
  const slopeTypeCount = stats.slopeTypeBreakdown?.length || 0;
  const severeRated = stats.severeRatedSlopes || 0;
  const uninspected = stats.slopesWithoutInspection || 0;
  const networkSevereCount = stats.networkWithMostSevereSlopes?.severe_count || 0;
  const sectionSevereCount = stats.sectionWithMostSevereSlopes?.severe_count || 0;

  // Sort rating breakdown by rating level value (highest first). If DB value isn't present,
  // derive from name (Rx/Cx) or fallback mapping.
  const sortedRatingBreakdown = [...(stats.ratingBreakdown || [])]
    .sort((a, b) => getRatingOrderValue(b) - getRatingOrderValue(a));
  const highestRiskRating = sortedRatingBreakdown.find(r => r.rating_name !== 'Unrated');
  const urgentAttentionCount = highestRiskRating?.count || 0;

  return {
    totalSlopes: {
      id: 'total_slopes',
      title: "Total Slopes",
      count: totalSlopes,
      progress: totalSlopes > 0 ? 100 : 0,
      description: `Infrastructure assets across all networks`,
      Icon: AccountBalanceIcon,
      color: 'primary.main',
      trend: totalSlopes > 0 ? `${totalSlopes} slopes` : '0'
    },
    slopeTypes: {
      id: 'slope_types',
      title: "Slope Subtypes",
      count: slopeTypeCount,
      progress: slopeTypeCount > 0 ? Math.min((slopeTypeCount / 10) * 100, 100) : 0,
      description: `${slopeTypeCount} different slope subtypes`,
      Icon: CategoryIcon,
      color: 'info.main',
      trend: slopeTypeCount > 0 ? `${slopeTypeCount} types` : '0',
      breakdown: stats.slopeTypeBreakdown,
      chartData: prepareSlopeTypeChartData(stats.slopeTypeBreakdown || []),
      cutEmbankmentData: prepareCutEmbankmentChartData(stats.cutEmbankmentBreakdown || [])
    },
    severeSlopes: {
      id: 'severe_slopes',
      title: "Hazard Ranking",
      count: urgentAttentionCount,
      progress: totalSlopes > 0 ? Math.round((urgentAttentionCount / totalSlopes) * 100) : 0,
      description: `${urgentAttentionCount} slopes need urgent attention`,
      Icon: WarningAmberIcon,
      color: 'error.main',
      trend: urgentAttentionCount > 0 ? `${urgentAttentionCount} critical` : 'All good',
      chartData: prepareRatingChartData(sortedRatingBreakdown)
    },
    uninspected: {
      id: 'uninspected_slopes',
      title: "Uninspected Slopes",
      count: uninspected,
      progress: totalSlopes > 0 ? Math.round((uninspected / totalSlopes) * 100) : 0,
      description: `Slopes pending initial inspection`,
      Icon: AssignmentLateIcon,
      color: 'warning.main',
      trend: uninspected > 0 ? 'Action needed' : 'Up to date',
      chartData: prepareInspectionStatusChartData(totalSlopes, uninspected),
      uninspectedBySubtype: prepareUninspectedBreakdownChartData(stats.uninspectedBySubtype || []),
      uninspectedByNetwork: stats.uninspectedByNetwork || [],
      uninspectedBySlopeType: prepareCutEmbankmentChartData(stats.uninspectedBySlopeType || [])
    },
    networkSevere: {
      id: 'network_severe',
      title: "Highest-Risk Network",
      count: networkSevereCount,
      progress: networkSevereCount > 0 ? Math.min((networkSevereCount / 5) * 100, 100) : 0, 
      description: stats.networkWithMostSevereSlopes?.network_name || 'N/A',
      Icon: PlaceIcon, 
      color: 'secondary.main', 
      trend: networkSevereCount > 0 ? `${networkSevereCount} severe` : '0'
    },
    sectionSevere: {
      id: 'section_severe',
      title: "Highest-Risk Section",
      count: sectionSevereCount,
      progress: sectionSevereCount > 0 ? Math.min((sectionSevereCount / 5) * 100, 100) : 0, 
      description: stats.sectionWithMostSevereSlopes?.section_name || 'N/A',
      Icon: LocationOnIcon, 
      color: 'secondary.main', 
      trend: sectionSevereCount > 0 ? `${sectionSevereCount} severe` : '0'
    },
  };
};

// Function to determine if a trend is positive or negative
const getTrendDirection = (trend) => {
  if (!trend) return 'neutral';
  return trend.startsWith('+') ? 'up' : trend.startsWith('-') ? 'down' : 'neutral';
};

const SlopeSummarySection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, getUserOrganization } = useAuth();
  const organizationId = getUserOrganization()?.id;
  
  const [stats, setStats] = useState({
    totalSlopes: 0,
    slopeTypeBreakdown: [],
    cutEmbankmentBreakdown: [],
    severeRatedSlopes: 0,
    ratingBreakdown: [],
    slopesWithoutInspection: 0,
    uninspectedBySubtype: [],
    uninspectedByNetwork: [],
    uninspectedBySlopeType: [],
    uninspectedByRating: [],
    networkWithMostSevereSlopes: { network_name: 'N/A', severe_count: 0 },
    sectionWithMostSevereSlopes: { section_name: 'N/A', severe_count: 0 },
    inspectionStatusBreakdown: [],
    totalInspections: 0
  });
  
  // Additional state for chart data
  const [ratingBreakdown, setRatingBreakdown] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [selectedSlopeType, setSelectedSlopeType] = useState('All Types');
  
  // Table data state for expanded cards
  const [tableData, setTableData] = useState({});
  const [tableLoading, setTableLoading] = useState({});
  const [tableError, setTableError] = useState({});
  
  // Pagination state for each table
  const [tablePagination, setTablePagination] = useState({});
  const [tableTotalCounts, setTableTotalCounts] = useState({});

  // CSV export in-progress flag (only one card is expanded at a time)
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  
  // Snackbar state for user notifications (align with SlopeAnalytics behavior)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  // Table filters state
  const [tableFilters, setTableFilters] = useState({
    slopeTypeCode: '',    // C or E (Cut/Embankment)
    ratingLevelId: '',    // Hazard ranking filter
    networkId: '',        // Network filter
    sectionId: ''         // Section filter
  });

  // Build a rating value -> color map from stats.ratingBreakdown (uses DB color_code)
  const ratingColorMap = useMemo(() => {
    const map = new Map();
    const list = Array.isArray(stats.ratingBreakdown) ? stats.ratingBreakdown : [];
    list.forEach((item) => {
      // Prefer numeric value if present; otherwise parse from name (Cx/Rx)
      let value = null;
      if (Number.isFinite(item.value)) value = item.value;
      if (value === null && Number.isFinite(item.rating_value)) value = item.rating_value;
      if (value === null) {
        const label = (item.rating_name || item.name || '').toString();
        const m = label.match(/(?:^|\s)(?:R|C)x?(\d+)/i);
        if (m && m[1]) value = parseInt(m[1], 10);
      }
      if (value === null && (item.rating_name || '').toLowerCase().includes('unrated')) value = 0;
      if (value !== null) {
        const hex = (item.color_code || item.colorCode || item.color || '').toString().trim();
        if (hex) map.set(value, hex);
      }
    });
    return map;
  }, [stats.ratingBreakdown]);

  const fetchSlopeStats = useCallback(async () => {
    if (!organizationId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🌄 [SlopeSummarySection] Fetching slope dashboard stats...');
      const response = await analyticsApi.getSlopeDashboardStats();
      
      const slopeStats = response.data.data || {};
      
      setStats({
        totalSlopes: slopeStats.totalSlopes || 0,
        slopeTypeBreakdown: slopeStats.slopeTypeBreakdown || [],
        cutEmbankmentBreakdown: slopeStats.cutEmbankmentBreakdown || [],
        severeRatedSlopes: slopeStats.severeRatedSlopes || 0,
        ratingBreakdown: slopeStats.ratingBreakdown || [],
        slopesWithoutInspection: slopeStats.slopesWithoutInspection || 0,
        uninspectedBySubtype: slopeStats.uninspectedBySubtype || [],
        uninspectedByNetwork: slopeStats.uninspectedByNetwork || [],
        uninspectedBySlopeType: slopeStats.uninspectedBySlopeType || [],
        uninspectedByRating: slopeStats.uninspectedByRating || [],
        networkWithMostSevereSlopes: slopeStats.networkWithMostSevereSlopes || { network_name: 'N/A', severe_count: 0 },
        sectionWithMostSevereSlopes: slopeStats.sectionWithMostSevereSlopes || { section_name: 'N/A', severe_count: 0 },
        inspectionStatusBreakdown: slopeStats.inspectionStatusBreakdown || [],
        totalInspections: slopeStats.totalInspections || 0
      });
      
      console.log('✅ [SlopeSummarySection] Slope stats loaded successfully:', slopeStats);
      
    } catch (err) {
      console.error('❌ [SlopeSummarySection] Failed to fetch slope stats:', err);
      setError('Failed to load slope statistics');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSlopeStats();
  }, [fetchSlopeStats]);

  // Generate slope data from real stats
  const slopeData = getSlopeDataFromStats(stats, loading);

  // Function to fetch table data for a specific card
  const fetchTableDataForCard = useCallback(async (cardId, page = 0, rowsPerPage = 10, slopeType = 'All Types', filters = {}) => {
    if (!organizationId) return;

    setTableLoading(prev => ({ ...prev, [cardId]: true }));
    setTableError(prev => ({ ...prev, [cardId]: null }));

    try {
      // Get the actual API card type from the slope data
      const apiCardType = slopeData[cardId]?.id || cardId;
      console.log(`🌄 [SlopeSummarySection] Fetching table data for card: ${cardId} (API: ${apiCardType}), filters:`, filters);

      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: 'created_at',
        sort_order: 'DESC'
      };

      if (cardId === 'slopeTypes' && slopeType !== 'All Types') {
        params.slopeType = slopeType;
      }

      // Apply additional filters
      if (filters.slopeTypeCode) {
        params.slopeTypeCode = filters.slopeTypeCode;
      }
      if (filters.ratingLevelId) {
        params.ratingLevelId = filters.ratingLevelId;
      }
      if (filters.networkId) {
        params.networkId = filters.networkId;
      }
      if (filters.sectionId) {
        params.sectionId = filters.sectionId;
      }

      const response = await analyticsApi.getSlopeDetails(apiCardType, params);
      const data = response.data.data || [];
      const total = response.data.total || 0;

      setTableData(prev => ({ ...prev, [cardId]: data }));
      setTableTotalCounts(prev => ({ ...prev, [cardId]: total }));

      console.log(`✅ [SlopeSummarySection] Table data loaded for ${cardId}: ${data.length} slopes, total: ${total}`);

    } catch (err) {
      console.error(`❌ [SlopeSummarySection] Failed to fetch table data for ${cardId}:`, err);
      setTableError(prev => ({
        ...prev,
        [cardId]: `Failed to load ${cardId} data`
      }));
    } finally {
      setTableLoading(prev => ({ ...prev, [cardId]: false }));
    }
  }, [organizationId, slopeData]);

  // Download the full filtered list (all pages, not just the visible rows) as CSV.
  // Re-fetches with limit = total so the export matches the active filters exactly.
  const handleDownloadCsv = useCallback(async (cardId) => {
    if (!organizationId) return;

    const apiCardType = slopeData[cardId]?.id || cardId;
    const total = tableTotalCounts[cardId] || 0;
    const slopeType = cardId === 'slopeTypes' ? selectedSlopeType : 'All Types';

    setDownloadingCsv(true);
    try {
      const params = {
        limit: total > 0 ? total : 1000, // grab every matching row
        offset: 0,
        sort_by: 'created_at',
        sort_order: 'DESC'
      };
      if (cardId === 'slopeTypes' && slopeType !== 'All Types') params.slopeType = slopeType;
      if (tableFilters.slopeTypeCode) params.slopeTypeCode = tableFilters.slopeTypeCode;
      if (tableFilters.ratingLevelId) params.ratingLevelId = tableFilters.ratingLevelId;
      if (tableFilters.networkId) params.networkId = tableFilters.networkId;
      if (tableFilters.sectionId) params.sectionId = tableFilters.sectionId;

      const response = await analyticsApi.getSlopeDetails(apiCardType, params);
      const rows = response.data.data || [];

      if (rows.length === 0) {
        setSnackbarMessage('No data to export for the current filters.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        return;
      }

      // Display the rating the same way the table does (Cx → Rx).
      const ratingLevel = (r) => {
        const lvl = r.rating?.level || 'Unrated';
        return lvl.startsWith('C') ? `R${lvl.substring(1)}` : lvl;
      };
      const cutEmbankment = (r) =>
        r.slopeTypeCode === 'C' ? 'Cut Slope' : r.slopeTypeCode === 'E' ? 'Embankment' : '';

      const columns = [
        { header: 'Slope ID', accessor: 'slopeId' },
        { header: 'Name', accessor: (r) => r.name || '' },
        { header: 'Code', accessor: (r) => r.code || '' },
        { header: 'Type', accessor: (r) => r.type || '' },
        { header: 'Cut/Embankment', accessor: cutEmbankment },
        { header: 'Location', accessor: (r) => r.location || '' },
        { header: 'H&R Ranking', accessor: ratingLevel },
        { header: 'Inspections', accessor: (r) => r.inspections?.count ?? 0 },
        { header: 'Latest Inspection', accessor: (r) => r.inspections?.latest || 'Never' },
        { header: 'Latest Condition', accessor: (r) => r.inspections?.condition || 'N/A' },
        { header: 'Coordinates', accessor: (r) => r.coordinates || '' }
      ];

      const title = (slopeData[cardId]?.title || 'slopes').toLowerCase().replace(/\s+/g, '_');
      const stamp = new Date().toISOString().slice(0, 10);
      exportToCsv(`slope_${title}_${stamp}.csv`, rows, columns);
    } catch (err) {
      console.error(`❌ [SlopeSummarySection] Failed to export CSV for ${cardId}:`, err);
      setSnackbarMessage('Failed to download CSV. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDownloadingCsv(false);
    }
  }, [organizationId, slopeData, tableTotalCounts, selectedSlopeType, tableFilters]);

  const handleCardHover = (cardId) => {
    // Disable hover effects when any card is expanded to prevent table blinking
    if (!expandedCardId) {
      setHoveredCardId(cardId);
    }
  };

  const handleExpandClick = (cardId) => {
    const newExpandedId = expandedCardId === cardId ? null : cardId;
    setExpandedCardId(newExpandedId);

    // Reset filters when expanding a new card
    if (newExpandedId) {
      setTableFilters({ slopeTypeCode: '', ratingLevelId: '', networkId: '', sectionId: '' });
    }

    // Initialize pagination state for new card
    if (newExpandedId && !tablePagination[cardId]) {
      setTablePagination(prev => ({
        ...prev,
        [cardId]: { page: 0, rowsPerPage: 10 }
      }));
    }

    // Fetch data when expanding a card
    if (newExpandedId && !tableData[cardId]) {
      const pagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
      fetchTableDataForCard(cardId, pagination.page, pagination.rowsPerPage, cardId === 'slopeTypes' ? selectedSlopeType : 'All Types', {});
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Pagination handlers
  const handleChangePage = (cardId, newPage) => {
    const currentPagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
    const updatedPagination = { ...currentPagination, page: newPage };

    setTablePagination(prev => ({
      ...prev,
      [cardId]: updatedPagination
    }));

    fetchTableDataForCard(cardId, newPage, currentPagination.rowsPerPage, cardId === 'slopeTypes' ? selectedSlopeType : 'All Types', tableFilters);
  };

  const handleChangeRowsPerPage = (cardId, newRowsPerPage) => {
    const updatedPagination = { page: 0, rowsPerPage: newRowsPerPage };

    setTablePagination(prev => ({
      ...prev,
      [cardId]: updatedPagination
    }));

    fetchTableDataForCard(cardId, 0, newRowsPerPage, cardId === 'slopeTypes' ? selectedSlopeType : 'All Types', tableFilters);
  };

  // Filter change handler
  const handleFilterChange = (cardId, filterName, value) => {
    const newFilters = { ...tableFilters, [filterName]: value };
    setTableFilters(newFilters);

    // Reset pagination and refetch with new filters
    setTablePagination(prev => ({
      ...prev,
      [cardId]: { page: 0, rowsPerPage: tablePagination[cardId]?.rowsPerPage || 10 }
    }));

    fetchTableDataForCard(cardId, 0, tablePagination[cardId]?.rowsPerPage || 10, cardId === 'slopeTypes' ? selectedSlopeType : 'All Types', newFilters);
  };

  // Reset all filters
  const handleResetFilters = (cardId) => {
    const emptyFilters = { slopeTypeCode: '', ratingLevelId: '', networkId: '', sectionId: '' };
    setTableFilters(emptyFilters);
    setSelectedSlopeType('All Types');

    setTablePagination(prev => ({
      ...prev,
      [cardId]: { page: 0, rowsPerPage: tablePagination[cardId]?.rowsPerPage || 10 }
    }));

    fetchTableDataForCard(cardId, 0, tablePagination[cardId]?.rowsPerPage || 10, 'All Types', emptyFilters);
  };

  const cardStyle = {
    p: 0,
    borderRadius: 2,
    minHeight: '150px', // Consistent minimum height like DefectSummarySection
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.08)',
    }
  };

  // Updated renderCard function matching DefectSummarySection pattern
  const renderCard = (cardId, data) => {
    const { title, count, progress, description, Icon, color, trend, slogan, breakdown, chartData, cutEmbankmentData, uninspectedBySubtype, uninspectedBySlopeType } = data;
    const colorParts = color.split('.');
    const cardColor = theme.palette[colorParts[0]]?.[colorParts[1] || 'main'] || colorParts[0]; 
    const isExpanded = expandedCardId === cardId;
    const isHovered = hoveredCardId === cardId;
    const trendDirection = getTrendDirection(trend);
    // Whether this card has a donut chart in its detail zone below the headline number
    const hasDonut = chartData && chartData.length > 0 &&
      ['slopeTypes', 'severeSlopes', 'uninspected'].includes(cardId);
    
    return (
      <Paper 
        sx={{
          ...cardStyle
        }}
        onMouseEnter={() => handleCardHover(cardId)}
        onMouseLeave={() => handleCardHover(null)}
      >
          {/* Header section — fixed height so all card headers align regardless of title length */}
          <Box
            sx={{
              px: 1.25,
              height: 46,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 0.5,
              borderBottom: '1px solid',
              borderColor: alpha(cardColor, 0.15),
              bgcolor: alpha(cardColor, 0.03)
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(cardColor, 0.1),
                  color: cardColor,
                  width: 26,
                  height: 26,
                  mr: 1,
                  flexShrink: 0
                }}
              >
                <Icon sx={{ fontSize: 15 }} />
              </Avatar>
              <Tooltip title={title} placement="top-start">
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.78rem' }}
                >
                  {title}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Count section — top-aligned so the headline number sits at the same Y on every card */}
          <Box sx={{ px: 1.5, pt: 1.25, pb: 1, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 0.5, mb: 0.25 }}>
              <Typography
                component="div"
                sx={{
                  fontWeight: 800,
                  fontSize: '2.75rem',
                  lineHeight: 1.1,
                  backgroundImage: `linear-gradient(135deg, ${cardColor}, ${alpha(cardColor, 0.7)})`,
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {typeof count === 'string' ? count : count.toLocaleString()}
              </Typography>

              {trend && (
                <Chip
                  size="small"
                  label={trend}
                  icon={trendDirection === 'up' ? <ArrowUpwardIcon fontSize="small" /> :
                         trendDirection === 'down' ? <ArrowDownwardIcon fontSize="small" /> : null}
                  sx={{
                    height: 18,
                    flexShrink: 0,
                    mb: 0.5,
                    maxWidth: '60%',
                    fontSize: '0.6rem',
                    bgcolor: alpha(trendDirection === 'up' ? theme.palette.success.main :
                                  trendDirection === 'down' ? theme.palette.error.main :
                                  theme.palette.grey[500], 0.1),
                    color: trendDirection === 'up' ? theme.palette.success.dark :
                           trendDirection === 'down' ? theme.palette.error.dark :
                           theme.palette.grey[700],
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.6 },
                    '& .MuiChip-icon': { fontSize: '0.65rem', ml: 0.4 }
                  }}
                />
              )}
            </Box>

            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: '0.68rem',
                lineHeight: 1.3,
                minHeight: '2.6em', // reserve 2 lines so all cards' detail zones align
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden'
              }}
            >
              {description}
            </Typography>

            {/* Donut chart for specific cards */}
            {hasDonut && (
              <Box sx={{ mt: 1, height: 60, display: 'flex', alignItems: 'flex-start' }}>
                <ResponsiveContainer width="40%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={12}
                      outerRadius={24}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name) => [`${value}`, name]}
                      labelStyle={{ fontSize: '0.7rem' }}
                      contentStyle={{
                        fontSize: '0.7rem',
                        padding: '4px 8px',
                        border: `1px solid ${alpha(cardColor, 0.2)}`,
                        borderRadius: 4
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ ml: 1, flex: 1 }}>
                  {chartData.slice(0, 3).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.2 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          mr: 0.5,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ fontSize: '0.6rem', flex: 1, minWidth: 0 }}
                      >
                        {item.name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.6rem', ml: 0.5, flexShrink: 0 }}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                  {chartData.length > 3 && (
                    <Tooltip
                      title={
                        <Box>
                          {chartData.slice(3).map((item, index) => (
                            <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: item.color, mr: 1 }} />
                                <Typography variant="caption">{item.name}</Typography>
                              </Box>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', ml: 2 }}>{item.value}</Typography>
                            </Box>
                          ))}
                        </Box>
                      }
                      arrow
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', fontStyle: 'italic', cursor: 'pointer' }}>
                        +{chartData.length - 3} more
                      </Typography>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}

            {/* Cut/Embankment breakdown for slopeTypes card */}
            {cardId === 'slopeTypes' && cutEmbankmentData && cutEmbankmentData.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.6rem', mb: 0.5, display: 'block' }}>
                  Cut / Embankment
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {cutEmbankmentData.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          mr: 0.5,
                          flexShrink: 0
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
                        {item.name}: {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Uninspected breakdown for uninspected card — styled to match the Cut/Embankment block */}
            {cardId === 'uninspected' && uninspectedBySubtype && uninspectedBySubtype.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.6rem', mb: 0.5, display: 'block' }}>
                  By Type
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
                  {uninspectedBySubtype.slice(0, 4).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: item.color,
                          mr: 0.5,
                          flexShrink: 0
                        }}
                      />
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
                        {item.name}: {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {uninspectedBySlopeType && uninspectedBySlopeType.length > 0 && (
                  <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}>
                    {uninspectedBySlopeType.map((item, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: item.color,
                            mr: 0.5,
                            flexShrink: 0
                          }}
                        />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 500 }}>
                          {item.name}: {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* Fallback breakdown for cards without charts */}
            {breakdown && breakdown.length > 0 && (!chartData || chartData.length === 0) && cardId !== 'slopeTypes' && cardId !== 'severeSlopes' && cardId !== 'uninspected' && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  Top Types:
                </Typography>
                <Box sx={{ mt: 0.25 }}>
                  {breakdown.slice(0, 3).map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {item.subtype_name}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                        {item.count}
                      </Typography>
                    </Box>
                  ))}
                  {breakdown.length > 3 && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', fontStyle: 'italic', mt: 0.1 }}>
                      +{breakdown.length - 3} more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Progress bar section */}
          <Box sx={{ px: 2, pb: 1.5 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 6, 
                borderRadius: 3,
                bgcolor: alpha(cardColor, 0.15), 
                '& .MuiLinearProgress-bar': {
                  bgcolor: cardColor,
                  borderRadius: 3,
                }
              }} 
            />
          </Box>
          
          {/* Expand Button */}
          <Fade in={isHovered || isExpanded}>
            <IconButton
              onClick={() => handleExpandClick(cardId)}
              sx={{
                position: 'absolute',
                bottom: 6,
                right: 6,
                bgcolor: alpha(theme.palette.background.paper, 0.9),
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
                '&:hover': {
                  bgcolor: theme.palette.background.paper,
                }
              }}
              size="small"
              aria-expanded={isExpanded}
              aria-label="show more"
            >
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          </Fade>
        </Paper>
    );
  };

  // Detail Table Component with Real Slope Data
  const DetailTable = ({ cardId, selectedSlopeType, setSelectedSlopeType }) => {
    const data = tableData[cardId] || [];
    const loading = tableLoading[cardId] || false;
    const error = tableError[cardId] || null;
    const title = slopeData[cardId]?.title || 'Slope Details';
    const count = slopeData[cardId]?.count || 0;
    const cardColor = slopeData[cardId]?.color || 'grey.600';
    const colorParts = cardColor.split('.');
    const mainColor = theme.palette[colorParts[0]]?.[colorParts[1] || 'main'] || colorParts[0];
    
    // Get pagination state for this card
    const pagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
    const totalCount = tableTotalCounts[cardId] || 0;

    // Show loading state
    if (loading) {
      return (
        <Fade in={true} timeout={400}>
          <Paper sx={{ 
            mt: 2, 
            borderRadius: 2, 
            overflow: 'hidden', 
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${alpha(mainColor, 0.2)}`,
            p: 4,
            textAlign: 'center'
          }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading {title.toLowerCase()} data...
            </Typography>
          </Paper>
        </Fade>
      );
    }

    // Show error state
    if (error) {
      return (
        <Fade in={true} timeout={400}>
          <Paper sx={{ 
            mt: 2, 
            borderRadius: 2, 
            overflow: 'hidden', 
            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            p: 4,
            textAlign: 'center'
          }}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Try again
            </Typography>
          </Paper>
        </Fade>
      );
    }

    // Handle View on Map action for slope assets
    const handleViewOnMap = async (slope, event) => {
      event.stopPropagation();

      try {
        const assetId = slope.id || slope.asset_id || slope.slope_id;

        // 1) Try parsing coordinates from API's string field "coordinates" (e.g., "lat, lng")
        let parsedLat = undefined;
        let parsedLng = undefined;
        if (typeof slope.coordinates === 'string' && slope.coordinates.includes(',')) {
          const parts = slope.coordinates.split(',').map(p => p.trim());
          const lat = parseFloat(parts[0]);
          const lng = parseFloat(parts[1]);
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            parsedLat = lat;
            parsedLng = lng;
          }
        }

        // 2) Build base assetData from row
        let assetData = {
          id: assetId,
          name: slope.name,
          code: slope.code || slope.slopeId,
          status: slope.status || 'active',
          center_lat: slope.center_lat ?? parsedLat,
          center_lng: slope.center_lng ?? parsedLng,
          subtype_name: slope.type || 'Slope',
          type_name: 'Slope',
          category_name: 'Geotechnical',
          rating_name: slope.rating?.level || 'N/A',
          rating_color_code: slope.rating?.color || theme.palette.grey[400],
          created_at: slope.created_at
        };

        // 3) If still missing, fallback to assets listing endpoint which computes centroid coordinates
        if (!assetData.center_lat || !assetData.center_lng) {
          if (!assetId) {
            console.warn('⚠️ [SlopeDashboard] Missing slope id for fetching coordinates');
            setSnackbarMessage(`Unable to show "${slope.name}" on map: Missing asset ID.`);
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
          }
          // Use listing endpoint to compute center_lat/center_lng via centroid
          const listResp = await assetsApi.getAll({ limit: 1, offset: 0, search: String(assetId) });
          const list = Array.isArray(listResp.data) ? listResp.data : [];
          const match = list.find(a => a.id === assetId) || list[0];
          if (match) {
            assetData = {
              ...assetData,
              name: match.name || assetData.name,
              code: match.code || assetData.code,
              center_lat: match.center_lat ?? assetData.center_lat,
              center_lng: match.center_lng ?? assetData.center_lng,
              subtype_name: match.subtype_name || assetData.subtype_name,
              type_name: match.type_name || assetData.type_name,
              category_name: match.category_name || assetData.category_name,
              rating_name: (match.rating_name || assetData.rating_name)?.replace('Cx', 'Rx'),
              rating_color_code: match.rating_color_code || assetData.rating_color_code,
            };
          }
        }

        // 4) Validate and navigate
        if (!assetData.center_lat || !assetData.center_lng) {
          setSnackbarMessage(`Unable to show "${assetData.name || 'Slope'}" on map: Slope location coordinates are not available in the database.`);
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }

        console.log('🗺️ [SlopeDashboard] Navigating to Map View with slope data:', assetData);
        navigate('/map-view', { state: { selectedAsset: assetData, source: 'assetsPage' } });
      } catch (err) {
        console.error('❌ [SlopeDashboard] Failed to fetch slope details for map view:', err);
        setSnackbarMessage('Failed to open on map. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    };

    const getRatingChip = (rating) => {
      // Convert Cx to Rx format for display
      let displayLevel = rating?.level;
      if (displayLevel && displayLevel.startsWith('C')) {
        displayLevel = 'R' + displayLevel.substring(1);
      }

      // Prefer DB-provided color code; fall back to known palette by name; finally grey
      const nameKey = (rating?.level || '').toString();
      const dbHex = (rating?.color_code || rating?.color || '').toString().trim();
      // Try to locate color by numeric value using ratingColorMap
      let numericValue = Number.isFinite(rating?.value) ? rating.value : undefined;
      if (numericValue === undefined) {
        const m = nameKey.match(/(?:^|\s)R?x?(\d+)/i);
        if (m && m[1]) numericValue = parseInt(m[1], 10);
      }
      const mappedHex = numericValue !== undefined ? (ratingColorMap.get(numericValue) || '') : '';
      const paletteHex = CHART_COLORS.ratings[nameKey] || CHART_COLORS.ratings.Unrated;
      const hex = dbHex || mappedHex || paletteHex || '#9e9e9e';

      return (
        <Chip
          size="small"
          label={displayLevel || 'Unrated'}
          sx={{
            backgroundColor: alpha(hex, 0.12),
            color: hex,
            border: `1px solid ${alpha(hex, 0.35)}`,
            fontWeight: 700,
            borderRadius: 1,
            fontSize: '0.7rem',
            height: 22
          }}
        />
      );
    };

    return (
      <Fade in={true} timeout={400}>
        <Paper sx={{ 
          mt: 2, 
          borderRadius: 2, 
          overflow: 'hidden', 
          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)',
          border: `1px solid ${alpha(mainColor, 0.2)}`
        }}>
          {/* Header with title */}
          <Box sx={{
            p: 2,
            pb: 1.5,
            bgcolor: alpha(mainColor, 0.05),
            borderBottom: `1px solid ${alpha(mainColor, 0.2)}`
          }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
              <Badge
                badgeContent={count}
                color={colorParts[0] !== 'grey' ? colorParts[0] : 'default'}
                sx={{ '& .MuiBadge-badge': { fontWeight: 600 } }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
              </Badge>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  label={`${data.length} of ${totalCount} items`}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 1.5 }}
                />
                <Tooltip title="Download the full filtered list as CSV">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={downloadingCsv ? <CircularProgress size={14} /> : <FileDownloadIcon />}
                      disabled={downloadingCsv || totalCount === 0}
                      onClick={() => handleDownloadCsv(cardId)}
                      sx={{
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        borderColor: alpha(mainColor, 0.3),
                        color: mainColor,
                        '&:hover': { borderColor: mainColor, bgcolor: alpha(mainColor, 0.05) }
                      }}
                    >
                      {downloadingCsv ? 'Preparing…' : 'Download CSV'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {/* Filters Row */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
              {/* Subtype Filter (for slopeTypes card) */}
              {cardId === 'slopeTypes' && (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Subtype</InputLabel>
                  <Select
                    value={selectedSlopeType}
                    onChange={(e) => {
                      const newSlopeType = e.target.value;
                      setSelectedSlopeType(newSlopeType);
                      const currentPagination = tablePagination[cardId] || { page: 0, rowsPerPage: 10 };
                      setTablePagination(prev => ({
                        ...prev,
                        [cardId]: { ...currentPagination, page: 0 }
                      }));
                      fetchTableDataForCard(cardId, 0, currentPagination.rowsPerPage, newSlopeType, tableFilters);
                    }}
                    label="Subtype"
                  >
                    <MenuItem value="All Types"><em>All Subtypes</em></MenuItem>
                    {slopeData.slopeTypes.breakdown?.map((type) => (
                      <MenuItem key={type.subtype_name} value={type.subtype_name}>
                        {type.subtype_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Cut/Embankment Filter */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Cut/Emb.</InputLabel>
                <Select
                  value={tableFilters.slopeTypeCode}
                  onChange={(e) => handleFilterChange(cardId, 'slopeTypeCode', e.target.value)}
                  label="Cut/Emb."
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="C">Cut Slope</MenuItem>
                  <MenuItem value="E">Embankment</MenuItem>
                </Select>
              </FormControl>

              {/* Hazard & Risk Ranking Filter - hide for severeSlopes card since it already shows ranking breakdown */}
              {cardId !== 'severeSlopes' && (
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>H&R Ranking</InputLabel>
                  <Select
                    value={tableFilters.ratingLevelId}
                    onChange={(e) => handleFilterChange(cardId, 'ratingLevelId', e.target.value)}
                    label="H&R Ranking"
                  >
                    <MenuItem value=""><em>All Rankings</em></MenuItem>
                    {/* Use uninspectedByRating for uninspected card, otherwise use ratingBreakdown */}
                    {(cardId === 'uninspected' ? stats.uninspectedByRating : stats.ratingBreakdown)?.map((rating, index) => (
                      <MenuItem
                        key={rating.rating_level_id || `unrated-${index}`}
                        value={rating.rating_level_id === null ? 'unrated' : rating.rating_level_id}
                      >
                        {(rating.rating_name || '').replace('Cx', 'Rx')} ({rating.count})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Reset Filters Button */}
              {(tableFilters.slopeTypeCode || tableFilters.ratingLevelId || tableFilters.networkId || tableFilters.sectionId || selectedSlopeType !== 'All Types') && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={() => handleResetFilters(cardId)}
                  sx={{
                    height: 40,
                    textTransform: 'none',
                    borderColor: alpha(mainColor, 0.3),
                    color: mainColor,
                    '&:hover': {
                      borderColor: mainColor,
                      bgcolor: alpha(mainColor, 0.05)
                    }
                  }}
                >
                  Reset
                </Button>
              )}
            </Stack>
          </Box>
          
          <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Slope ID</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Cut/Emb.</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Location</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>H&R Ranking</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Inspections</TableCell>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Latest Inspection</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.length > 0 ? (
                  data
                    .filter(slope => selectedSlopeType === 'All Types' || slope.type === selectedSlopeType)
                    .map((slope, index) => (
                    <TableRow
                      key={`${slope.id}-${index}`}
                      hover
                      sx={{
                        '&:hover td': { bgcolor: alpha(mainColor, 0.02) },
                        '&:nth-of-type(odd)': { bgcolor: alpha(theme.palette.grey[50], 0.5) },
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: alpha(mainColor, 0.08),
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                        {slope.slopeId}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 150 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {slope.name}
                        </Typography>
                        {slope.code && (
                          <Typography variant="caption" color="text.secondary">
                            {slope.code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {slope.type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {slope.slopeTypeName ? (
                          <Chip
                            size="small"
                            label={slope.slopeTypeCode === 'C' ? 'Cut' : slope.slopeTypeCode === 'E' ? 'Emb' : '-'}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: slope.slopeTypeCode === 'C'
                                ? alpha(CHART_COLORS.cutEmbankment['Cut Slope'], 0.12)
                                : slope.slopeTypeCode === 'E'
                                  ? alpha(CHART_COLORS.cutEmbankment['Embankment'], 0.12)
                                  : alpha(theme.palette.grey[500], 0.12),
                              color: slope.slopeTypeCode === 'C'
                                ? CHART_COLORS.cutEmbankment['Cut Slope']
                                : slope.slopeTypeCode === 'E'
                                  ? CHART_COLORS.cutEmbankment['Embankment']
                                  : theme.palette.grey[600],
                              '& .MuiChip-label': { px: 0.75 }
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="caption" sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {slope.location}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getRatingChip(slope.rating)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {slope.inspections.count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {slope.inspections.latest}
                        </Typography>
                        {slope.inspections.condition !== 'N/A' && (
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem' }}>
                            {slope.inspections.condition}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View on Map">
                          <IconButton
                            size="small"
                            onClick={(e) => handleViewOnMap(slope, e)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No {title.toLowerCase()} found
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Try refreshing or check if there are any slopes in your organization
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Snackbar for user notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbarSeverity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          
          {/* Sticky Pagination at bottom */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={(event, newPage) => handleChangePage(cardId, newPage)}
            onRowsPerPageChange={(event) => handleChangeRowsPerPage(cardId, parseInt(event.target.value, 10))}
            showFirstButton
            showLastButton
            sx={{
              position: 'sticky',
              bottom: 0,
              borderTop: 1,
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              zIndex: 1,
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
            }}
          />
        </Paper>
      </Fade>
    );
  };

  // Don't render if not authenticated or no organization
  if (!isAuthenticated || !organizationId) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary">
          {!isAuthenticated ? 'Authentication required' : 'No organization access'}
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Unable to load slope statistics
        </Typography>
      </Box>
    );
  }

  return (
    <Box> 
      {/* Header with refresh info */}
      {!loading && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Real-time slope infrastructure data • Last updated: {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      )}

      <Box 
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(6, 1fr)',
            xl: 'repeat(6, 1fr)'
          },
          gap: 1.5,
          mb: 3
        }}
      > 
        {Object.entries(slopeData).map(([key, data]) => (
          <Box key={key}>
            {renderCard(key, data)}
          </Box>
        ))}
      </Box>
      
      <Collapse in={Boolean(expandedCardId)} timeout="auto" unmountOnExit>
        {expandedCardId ? (
          <DetailTable cardId={expandedCardId} selectedSlopeType={selectedSlopeType} setSelectedSlopeType={setSelectedSlopeType} />
        ) : null}
      </Collapse>
    </Box>
  );
};

export default SlopeSummarySection;